<template>
  <v-container fluid>
    <v-row class="mb-4">
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold">Integrações</h1>
        <p class="text-subtitle-1 grey--text">
          Gerencie suas integrações com plataformas externas
        </p>
      </v-col>
    </v-row>

    <v-row style="max-width: 1200px; margin: 0 auto;">
      <v-col
        v-for="integration in integrations"
        :key="integration.provider"
        cols="12"
        lg="4"
        md="6"
        sm="12"
      >
        <IntegrationCard
          :integration="integration"
          :is-active="isActive(integration.provider)"
          @configure="configureIntegration"
          @manage="manageIntegration"
        />
      </v-col>
    </v-row>

    <v-row class="my-4">
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold">Gerenciar token</h1>
        <p class="text-subtitle-1 grey--text">
          Gerencie seu token de acesso para integrações.
        </p>
      </v-col>
    </v-row>

    <v-row align="center">
      <v-col cols="auto">
        <v-btn
          color="primary"
          :loading="generatingToken"
          @click="generateToken"
        >
          Gerar novo token
        </v-btn>
      </v-col>
      <v-col cols="12" md="6">
        <div class="d-flex align-center">
          <v-text-field
            v-model="accessToken"
            label="Token de acesso"
            readonly
            outlined
            dense
            hide-details
            class="mr-2"
          />
          <v-btn icon @click="copyToken" :disabled="!accessToken">
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col cols="12">
        <ApiDocumentation :access-token="accessToken" />
      </v-col>
    </v-row>

    <IntegrationConfigModal ref="configModal" @saved="handleIntegrationSaved" />

    <v-dialog v-model="confirmTokenDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h6">
          Gerar novo token
        </v-card-title>
        <v-card-text>
          Tem certeza que deseja gerar um novo token? O token atual será
          invalidado e todas as integrações que o utilizam deixarão de
          funcionar.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmTokenDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="generatingToken"
            @click="confirmGenerateToken"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import integrationsOptions from "@/enums/integrations/integrationsOptions.json";
import IntegrationCard from "@/components/product/ui/IntegrationCard.vue";
import IntegrationConfigModal from "@/components/product/forms/IntegrationConfigModal.vue";
import ApiDocumentation from "@/components/integration/ApiDocumentation.vue";

export default {
  components: {
    IntegrationCard,
    IntegrationConfigModal,
    ApiDocumentation,
  },

  data() {
    return {
      integrations: integrationsOptions,
      activeIntegrations: [],
      loading: false,
      accessToken: null,
      generatingToken: false,
      confirmTokenDialog: false,
    };
  },

  mounted() {
    this.loadActiveIntegrations();
    this.loadToken();
  },

  methods: {
    async loadActiveIntegrations() {
      this.loading = true;

      try {
        const response = await this.$http.index("integration/integration");

        this.activeIntegrations = response.integrations;
      } catch (error) {
        console.error("Erro ao carregar integrações:", error);
      } finally {
        this.loading = false;
      }
    },

    async loadToken() {
      try {
        const response = await this.$http.index("integration/token");
        this.accessToken =
          response.integration?.credentials?.access_token || null;
      } catch (error) {
        console.error("Erro ao carregar token:", error);
      }
    },

    generateToken() {
      this.confirmTokenDialog = true;
    },

    async confirmGenerateToken() {
      this.generatingToken = true;

      try {
        const response = await this.$http.store("integration/token");
        this.accessToken = response.integration?.credentials?.access_token;
      } catch (error) {
        console.error("Erro ao gerar token:", error);
      } finally {
        this.generatingToken = false;
        this.confirmTokenDialog = false;
      }
    },

    copyToken() {
      if (this.accessToken) {
        navigator.clipboard.writeText(this.accessToken);
      }
    },

    isActive(provider) {
      return this.activeIntegrations.some((int) => int.provider === provider);
    },

    configureIntegration(integration) {
      this.$refs.configModal.open(integration);
    },

    manageIntegration(integration) {
      const existingData = this.activeIntegrations.find(
        (int) => int.provider === integration.provider,
      );
      this.$refs.configModal.open(integration, existingData);
    },

    async handleIntegrationSaved(payload) {
      try {
        const existingIntegration = this.activeIntegrations.find(
          (int) => int.provider === payload.provider,
        );

        if (existingIntegration) {
          await this.$http.update(
            "integration/integration",
            existingIntegration.id,
            payload,
          );
        } else {
          await this.$http.store("integration/integration", payload);
        }

        await this.loadActiveIntegrations();
      } catch (error) {
        console.error("Erro ao salvar integração:", error);
      }
    },
  },
};
</script>
